<template>
    <div>
        <div
            ref="dropdowns"
            class="dropdowns"
            tabindex="-1"
        >
            <radio-buttons
                list-heading="App.RadioButtonListHeader.SelectTopic"
                :radio-button-list="radioButtonListValues"
                :default-radio-button="defaultValue"
                :sub-topic="subTopic"
                :select-by-url="true"
                @selected-radio-button="handleSelectedRadioButton"
            />
        </div>
        <div class="search-components">
            <search-address />
            <locate-me />
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch, Prop } from "vue-facing-decorator"
    import SearchAddress from "@/components/SearchAddress.vue"
    import LocateMeLink from "@/components/LocateMeLink.vue"
    import RadioButtonListWithDropdown from "@/components/RadioButtonListWithDropdown.vue"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import CheckboxList from "@/components/CheckboxList.vue"
    import SearchResultModel from "@/models/searchresultmodel"
    import { store } from '@/store/store'

    @Component({
        components: {
            "radio-buttons": RadioButtonListWithDropdown,
            "checkbox-list": CheckboxList,
            "locate-me": LocateMeLink,
            "search-address": SearchAddress
        }
    })
    export default class Infokartta extends Vue {
        @Prop({ type: String }) subTopic!: string

        // DataPublishServices rajapintakutsun etuosa
        apiCallBasePart: string = "v1/Infokartta/"

        pageName: string = "infokartatliikenne"
        radioButtonGroup: string = "LiikenneInfokartat"

        radioButtonList: RadioButtonModel[] = this.radioButtonListValues

        // Valittu radiobutton tietoineen
        selectedRadioButtonData: RadioButtonModel = this.radioButtonListValues[0]
        infokartatStatesta: any = []

        async activated() {
            store.dispatch("setCurrentPage", this.pageName)
            // Tehdään apikutsu, jolla haetaan Infokartan tyypillä infokartat radio button listaukseen
            await this.getInfokartat()
            this.selectedRadioButtonData = this.defaultRadioButton
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        deactivated() {
            store.dispatch("setCurrentPage", "")
            store.dispatch("setSubTopic", null)
            this.radioButtonList = []
            store.dispatch("setInfokarttaRadioValues", [])
        }

        get currentPage(): string {
            return store.getters.currentPage
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        get defaultValue(): string | undefined {
            if (!this.defaultRadioButton) {
                return undefined
            }
            return this.defaultRadioButton.labelText
        }

        // kielivalinta muuttunut. nyt pitäisi lataa data uudestaan ja käyttäjän tekemä mahdollinen valinta....
        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // Kielivalinta muuttunut. Ladataan data uudestaan.
            if (this.currentPage !== this.pageName) {
                return
            }
            this.setDataToLoad(this.selectedRadioButtonData)
        }

        // haetaan infokartat tyypin perusteella (liikenne/viestintä)
        async getInfokartat() {
            let infokartatData = ""

            infokartatData = store.getters.liikenneInfokarttaRadioValues

            store.dispatch("setInfokarttaRadioValues", infokartatData)
            this.infokartatStatesta = store.getters.infokarttaRadioValues
            this.radioButtonList.splice(0)

            this.infokartatStatesta.forEach(element => {
                if (this.activeLang.toString() === "fi") {
                    this.radioButtonList.push(new RadioButtonModel(element.nimifi, element.tunniste, "Infokartat"))
                }
                else if (this.activeLang.toString() === "sv") {
                    this.radioButtonList.push(new RadioButtonModel(element.nimisv, element.tunniste, "Infokartat"))
                }
                else {
                    this.radioButtonList.push(new RadioButtonModel(element.nimien, element.tunniste, "Infokartat"))
                }
            })
            store.dispatch("setSelectedSubjectRadioButton", this.defaultRadioButton)
            this.selectedRadioButtonData = this.defaultRadioButton
        }

        async handleSelectedRadioButton(item: RadioButtonModel) {
            this.selectedRadioButtonData = item
            // Tehdään apikutsu, jolla haetaan kartan data
            store.dispatch("setSelectedSubjectRadioButton", new RadioButtonModel(this.selectedRadioButtonData.labelText, this.selectedRadioButtonData.radioValue, this.selectedRadioButtonData.groupName, null, "0"))
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        async setDataToLoad(selectedRadio: RadioButtonModel): Promise<void> {
            if (selectedRadio !== this.selectedRadioButtonData) {
                return
            }

            if (this.currentPage !== this.pageName) {
                return
            }

            store.dispatch("setLoading", { name: "infokartta", value: true })
            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", "Infokartat")

            const clearLocationMarker = store.getters.dataUrlToLoad?.url?.split("/")[1]?.toLowerCase() !== "infokartta"

            // mikäli infokartta on valittu haetaan sen tiedot tunnisteella
            if (selectedRadio) {
                store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "Infokartta" + "?tunniste=" + selectedRadio.radioValue, type: "Infokartta", clearLocationMarker })
            }
            store.dispatch("setLoading", { name: "infokartta", value: false })
        }

        get leftSlotHeaderInfokartta(): string | undefined {
            return this.$t("App.LeftSlot.Header.Infokartat")
        }

        get defaultRadioButton(): RadioButtonModel {
            // Jos sivulle tullaan subTopic routen kautta, niin asetetaan default radiobutton value routen mukaisesti
            if (this.subTopic) {
                for (let i = 0; i < this.radioButtonList.length; i++) {
                    if (this.radioButtonList[i].radioValue === this.subTopic) {
                        return this.radioButtonList[i]
                    }
                }
            }
            return this.radioButtonList[0]
        }

        get urlParameterInRadioValues(): boolean {
            if (this.subTopic) {
                for (const element of this.infokartatStatesta) {
                    if (element.tunniste === this.subTopic) {
                        return true
                    }
                }
            }
            return false
        }

        updated() {
            this.$nextTick(() => {
                (this.$refs.dropdowns as HTMLHeadingElement).focus()
            })
        }

        get radioButtonListValues(): Array<RadioButtonModel> {
            const radioButtonValues = store.getters.infokarttaRadioValues ?? new RadioButtonModel("", "")
            const list: RadioButtonModel[] = []
            radioButtonValues.forEach(element => {
                if (this.activeLang.toString() === "fi") {
                    list.push(new RadioButtonModel(element.nimifi, element.tunniste, this.radioButtonGroup))
                }
                else if (this.activeLang.toString() === "sv") {
                    list.push(new RadioButtonModel(element.nimisv, element.tunniste, this.radioButtonGroup))
                }
                else {
                    list.push(new RadioButtonModel(element.nimien, element.tunniste, this.radioButtonGroup))
                }
            })
            return list ?? [new RadioButtonModel("", "")]
        }
    }
</script>
<style scoped>
    .dropdowns {
        margin-bottom: 2.5rem;
    }
</style>
